import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import DonationFilterDrawer from "../DonationFilterDrawer";
import DataTable from "../../../shared/DataTable/DataTable";

function matchesConditions(donation, conditions) {
  if (!conditions || conditions.length === 0) return true;
  const amt = parseFloat(donation.amount);
  return conditions.every((c) => {
    if (c.operator === "between") {
      const min = parseFloat(c.min);
      const max = parseFloat(c.max);
      if (isNaN(min) && isNaN(max)) return true;
      if (!isNaN(min) && !isNaN(max)) return amt >= min && amt <= max;
      if (!isNaN(min)) return amt >= min;
      return amt <= max;
    }
    const val = parseFloat(c.value);
    if (isNaN(val)) return true;
    if (c.operator === "equals") return amt === val;
    if (c.operator === "greater_than") return amt > val;
    if (c.operator === "less_than") return amt < val;
    return true;
  });
}

export default function NonprofitDonationTable({ data, name }) {
  const [selectedIds, setSelectedIds] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  const filteredData = useMemo(
    () => data.filter((d) => matchesConditions(d, appliedFilters.conditions)),
    [data, appliedFilters]
  );

  const columns = useMemo(() => [
    {
      id: "select",
      header: () => null,
      cell: ({ row }) => {
        const id = row.original.pid ?? row.index;
        return (
          <input
            type="checkbox"
            checked={selectedIds.includes(id)}
            onChange={() =>
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            className="w-4 h-4 rounded border-gray-300 accent-blue cursor-pointer"
          />
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
    {
      id: "first_name",
      accessorFn: (row) => row.donor?.first_name ?? "",
      header: "First Name",
    },
    {
      id: "last_name",
      accessorFn: (row) => row.donor?.last_name ?? "",
      header: "Last Name",
    },
    {
      accessorKey: "amount",
      header: "Amount",
      cell: ({ getValue }) => `$${getValue()}`,
    },
    {
      accessorKey: "pid",
      header: "Payment ID",
      enableGlobalFilter: false,
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => {
        const id = row.original.pid ?? row.index;
        return (
          <div className="text-center relative">
            <button
              onClick={() => setOpenMenuId(openMenuId === id ? null : id)}
              className="text-gray-500 hover:text-gray-800 bg-transparent border-none outline-none"
            >
              <FontAwesomeIcon icon={faEllipsis} />
            </button>
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
  ], [selectedIds, openMenuId]);

  const filterSlot = (
    <>
      <button
        onClick={() => setFilterOpen(true)}
        className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
      >
        <FontAwesomeIcon icon={faSliders} size="sm" />
        Filter
      </button>
      <DonationFilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        campaignName={name}
        appliedFilters={appliedFilters}
        onApply={(filters) => setAppliedFilters(filters)}
      />
    </>
  );

  return (
    <div className="bg-white rounded-2xl shadow-md overflow-hidden mb-6">
      <div className="px-6 pt-6">
        <h2 className="!font-poppins !font-bold !text-[22px] text-gray-900 !text-left mb-4">
          {name}
        </h2>
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        filterPlaceholder="Search by donor name"
        filterSlot={filterSlot}
        totalLabel="Total Donations"
        emptyMessage="No donations found."
      />
    </div>
  );
}
